import { useEffect, useState } from "react";
import { AttachmentPreviewModal, PreviewAttachment, isPreviewable } from "./AttachmentPreviewModal";

interface PreviewState {
  list: PreviewAttachment[];
  index: number;
}

type Listener = (state: PreviewState | null) => void;
const listeners = new Set<Listener>();
let current: PreviewState | null = null;

const emit = () => {
  listeners.forEach((l) => l(current));
};

export const openAttachmentPreview = (
  attOrList: PreviewAttachment | PreviewAttachment[],
  index = 0
) => {
  if (Array.isArray(attOrList)) {
    if (!attOrList.length) return;
    current = { list: attOrList, index: Math.max(0, Math.min(index, attOrList.length - 1)) };
  } else {
    current = { list: [attOrList], index: 0 };
  }
  emit();
};

export const closeAttachmentPreview = () => {
  current = null;
  emit();
};

const stepPreview = (delta: number) => {
  if (!current) return;
  const len = current.list.length;
  if (len < 2) return;
  current = { ...current, index: (current.index + delta + len) % len };
  emit();
};

export { isPreviewable };

/**
 * Mount once near the app root. Listens for openAttachmentPreview() calls
 * from anywhere and renders a single shared preview dialog with prev/next
 * navigation when multiple attachments are passed.
 */
export const AttachmentPreviewHost = () => {
  const [state, setState] = useState<PreviewState | null>(current);

  useEffect(() => {
    const l: Listener = (s) => setState(s);
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!state || state.list.length < 2) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        stepPreview(1);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        stepPreview(-1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state]);

  const att = state ? state.list[state.index] : null;

  return (
    <AttachmentPreviewModal
      attachment={att}
      open={!!att}
      onOpenChange={(o) => {
        if (!o) closeAttachmentPreview();
      }}
      total={state?.list.length ?? 0}
      index={state?.index ?? 0}
      onPrev={state && state.list.length > 1 ? () => stepPreview(-1) : undefined}
      onNext={state && state.list.length > 1 ? () => stepPreview(1) : undefined}
    />
  );
};
